import { Component, ChangeDetectorRef, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { BackHeaderComponent } from '@app/back-header/back-header.component';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatListModule } from '@angular/material/list';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatChipsModule } from '@angular/material/chips';
import { DocumentsApiService } from '@app/services/documents-api.service';
import { DocumentsInterface, CategoryCode } from '@app/interface/documents';
import { Organizations } from '@app/interface/organizations';
import { OrganizationApi } from '@app/services/organization-api.service';
import { RecursiveInterface } from '@app/services/base-authenticated.service';
import { PaymentInterface } from '@app/interface/payment';
import { ViewPaymentDialogComponent } from '@app/view-payment-dialog/view-payment-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmUploadDialogComponent } from '@app/confirm-upload-dialog/confirm-upload-dialog.component';
import { SubscriptionInterface } from '@app/interface/subscription';
import { PaymentApiService } from '@app/services/payment-api.service';
import { SubscriptionApiService } from '@app/services/subscription-api.service';
import { ToastService } from '@app/services/toast.service';

interface DocumentCategoryGroup {
  category: CategoryCode;
  label: string;
  icon: string;
  documents: DocumentsInterface[];
}

@Component({
  selector: 'izy-documents-table',
  imports: [
    BackHeaderComponent,
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatListModule,
    MatTooltipModule,
    MatChipsModule,
  ],
  templateUrl: './documents-table.component.html',
  styleUrl: './documents-table.component.scss',
})
export class DocumentsTableComponent implements OnInit {
  private toast = inject(ToastService);
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  documentsList: DocumentsInterface[] = [];
  isLoading = true;
  isUploading = false;
  organization_data: Organizations | null = null;
  subscription_data: SubscriptionInterface | undefined;
  payment_data: PaymentInterface | null = null;

  /** Ordine di visualizzazione delle sezioni per categoria. */
  private readonly categoryOrder: CategoryCode[] = [
    'GENERIC',
    'F24',
    'PARTITAIVA',
    'ID',
    'CONTRACT',
  ];

  /** Titolo mostrato per ogni sezione. */
  private readonly categoryLabels: Record<CategoryCode, string> = {
    GENERIC: 'Documenti generici',
    F24: 'F24',
    PARTITAIVA: 'Partita IVA',
    ID: "Documenti d'identità",
    CONTRACT: 'Contratti',
  };

  /** Icona rappresentativa per ogni sezione. */
  private readonly categoryIcons: Record<CategoryCode, string> = {
    GENERIC: 'description',
    F24: 'receipt_long',
    PARTITAIVA: 'badge',
    ID: 'perm_identity',
    CONTRACT: 'assignment',
  };

  /** Categoria selezionata dalle chip di filtro ('all' = tutte). */
  selectedCategory: CategoryCode | 'all' = 'all';

  /**
   * Tutti i documenti raggruppati per categoria (cache stabile).
   * Ricostruito solo quando cambiano i dati, per evitare che i binding
   * ricevano nuovi riferimenti a ogni ciclo di change detection.
   */
  private allDocumentGroups: DocumentCategoryGroup[] = [];

  /** Sezioni visibili in base alla chip selezionata (cache stabile). */
  documentGroups: DocumentCategoryGroup[] = [];

  /** Opzioni per le chip di filtro: "Tutti" + le categorie con documenti. */
  categoryFilterOptions: { value: CategoryCode | 'all'; name: string }[] = [];

  /**
   * Ricostruisce i gruppi di documenti e le opzioni di filtro a partire dai
   * dati correnti. Il contratto firmato dell'organizzazione (se presente)
   * confluisce nella sezione CONTRACT. Le sezioni vuote vengono escluse.
   */
  private rebuildDocumentGroups(): void {
    this.allDocumentGroups = this.categoryOrder
      .map((category) => {
        const documents = this.documentsList.filter(
          (doc) => doc.category === category,
        );

        if (category === 'CONTRACT' && this.hasContractFile()) {
          documents.push({
            name: 'Contratto firmato',
            file: this.organization_data!.contract_file!,
            is_uploaded_by_user: false,
            category: 'CONTRACT',
            organization_id: this.organization_data!.id,
          });
        }

        return {
          category,
          label: this.categoryLabels[category],
          icon: this.categoryIcons[category],
          documents,
        };
      })
      .filter((group) => group.documents.length > 0);

    this.categoryFilterOptions = [
      { value: 'all', name: 'Tutti' },
      ...this.allDocumentGroups.map((group) => ({
        value: group.category,
        name: group.label,
      })),
    ];

    // Se la categoria selezionata non esiste più, torniamo a "Tutti".
    if (
      this.selectedCategory !== 'all' &&
      !this.allDocumentGroups.some((g) => g.category === this.selectedCategory)
    ) {
      this.selectedCategory = 'all';
    }

    this.updateVisibleGroups();
  }

  /** Applica il filtro corrente alla lista dei gruppi visibili. */
  private updateVisibleGroups(): void {
    this.documentGroups =
      this.selectedCategory === 'all'
        ? this.allDocumentGroups
        : this.allDocumentGroups.filter(
            (group) => group.category === this.selectedCategory,
          );
  }

  applyCategoryFilter(value: CategoryCode | 'all' | null | undefined): void {
    // Deselezionando una chip il listbox emette valore vuoto: torniamo a "Tutti".
    this.selectedCategory = value ?? 'all';
    this.updateVisibleGroups();
    this.cdr.detectChanges();
  }

  /** trackBy per le sezioni: evita di ricreare i nodi a ogni CD. */
  trackByCategory = (_: number, group: DocumentCategoryGroup): string =>
    group.category;

  /** trackBy per le chip di filtro. */
  trackByFilterValue = (
    _: number,
    option: { value: CategoryCode | 'all' },
  ): string => option.value;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private incomeStatementService: DocumentsApiService,
    private cdr: ChangeDetectorRef,
    private subscriptionService: SubscriptionApiService,
    private organizationService: OrganizationApi,
    private dialog: MatDialog,
    private paymentService: PaymentApiService,
  ) {}

  ngOnInit(): void {
    this.loadDocumentStatements();
    this.getOrganization();
  }

  loadDocumentStatements(): void {
    const organization_id = Number(
      this.route.snapshot.paramMap.get('organizationId'),
    );

    this.incomeStatementService.getAllDocuments(organization_id).subscribe({
      next: (response) => {
        this.documentsList = response.results || [];
        this.rebuildDocumentGroups();
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error loading documents:', error);
        this.toast.error('Errore nel caricamento dei documenti');
        this.isLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  private getOrganization(): void {
    const organization_id = Number(
      this.route.snapshot.paramMap.get('organizationId'),
    );

    if (organization_id && !isNaN(organization_id)) {
      const filterParams = {
        organization_id: organization_id,
      };

      this.organizationService
        .getOrganization<Organizations>(organization_id, filterParams)
        .subscribe({
          next: (organization_data: Organizations) => {
            this.organization_data = organization_data;
            this.rebuildDocumentGroups();
            this.cdr.detectChanges();
          },
          error: (err: any) => {
            console.error('Error loading organization:', err);
            this.toast.error("Errore nel caricamento dell'organizzazione");
            this.cdr.detectChanges();
          },
        });
    } else {
      console.error(
        'Could not extract valid organization ID from URL or route parameters',
      );
    }
  }

  openAttachment(attachmentUrl: string): void {
    if (attachmentUrl) {
      window.open(attachmentUrl, '_blank');
    } else {
      this.showErrorMessage('Nessun allegato disponibile');
    }
  }

  hasContractFile(): boolean {
    return !!this.organization_data?.contract_file;
  }

  triggerFileUpload(): void {
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const file = input.files[0];
    const fileSizeLabel = this.formatFileSize(file.size);

    const dialogRef = this.dialog.open(ConfirmUploadDialogComponent, {
      width: '90vw',
      maxWidth: '420px',
      panelClass: 'izy-confirm-dialog-panel',
      data: { fileName: file.name, fileSize: fileSizeLabel },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      input.value = '';
      if (!confirmed) return;

      const organization_id = Number(
        this.route.snapshot.paramMap.get('organizationId'),
      );

      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', file.name);
      formData.append('organization_id', organization_id.toString());
      formData.append('is_uploaded_by_user', 'true');

      this.isUploading = true;
      this.cdr.detectChanges();

      this.incomeStatementService.uploadDocument(organization_id, formData).subscribe({
        next: () => {
          this.isUploading = false;
          this.toast.success('Documento caricato con successo');
          this.loadDocumentStatements();
          this.cdr.detectChanges();
        },
        error: (err: any) => {
          console.error('Error uploading document:', err);
          this.isUploading = false;
          this.toast.error('Errore nel caricamento del documento');
          this.cdr.detectChanges();
        },
      });
    });
  }

  private formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  backToDashboard(): void {
    const org_id = this.route.snapshot.paramMap.get('organizationId');
    this.router.navigate([`/${org_id}/dashboard`]);
  }

  private showErrorMessage(message: string): void {
    this.toast.error(message);
  }
}
